import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import api from "@/lib/api";

import LoginPage from "@/pages/Login";
import MRDashboard from "@/pages/mr/Dashboard";
import MRGenerate from "@/pages/mr/Generate";
import MRHistory from "@/pages/mr/History";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminEmployees from "@/pages/admin/Employees";
import AdminTemplates from "@/pages/admin/Templates";
import AdminLanguages from "@/pages/admin/Languages";
import AdminReports from "@/pages/admin/Reports";
import AdminAudit from "@/pages/admin/AuditLogs";
import AdminPositions from "@/pages/admin/Positions";
import AdminPosterRecords from "@/pages/admin/PosterRecords";
import AdminSettings from "@/pages/admin/Settings";
import AdminGenerate from "@/pages/admin/Generate";

import "@/App.css";

function TitleManager() {
  useEffect(() => {
    if (process.env.REACT_APP_SITE_TITLE) {
      document.title = process.env.REACT_APP_SITE_TITLE;
    }
    api.get("/settings").then(({ data }) => {
      if (data.site_title) document.title = data.site_title;
    }).catch(() => {});
  }, []);
  return null;
}

function Protected({ children, requireAdmin = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-500" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (requireAdmin && user.role !== "admin") return <Navigate to="/mr" replace />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/mr"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TitleManager />
        <div className="App">
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<LoginPage />} />

            {/* MR */}
            <Route path="/mr" element={<Protected><MRDashboard /></Protected>} />
            <Route path="/mr/generate" element={<Protected><MRGenerate /></Protected>} />
            <Route path="/mr/history" element={<Protected><MRHistory /></Protected>} />

            {/* Admin */}
            <Route path="/admin" element={<Protected requireAdmin><AdminDashboard /></Protected>} />
            <Route path="/admin/generate" element={<Protected requireAdmin><AdminGenerate /></Protected>} />
            <Route path="/admin/employees" element={<Protected requireAdmin><AdminEmployees /></Protected>} />
            <Route path="/admin/templates" element={<Protected requireAdmin><AdminTemplates /></Protected>} />
            <Route path="/admin/templates/:id/positions" element={<Protected requireAdmin><AdminPositions /></Protected>} />
            <Route path="/admin/languages" element={<Protected requireAdmin><AdminLanguages /></Protected>} />
            <Route path="/admin/reports" element={<Protected requireAdmin><AdminReports /></Protected>} />
            <Route path="/admin/audit" element={<Protected requireAdmin><AdminAudit /></Protected>} />
            <Route path="/admin/posters" element={<Protected requireAdmin><AdminPosterRecords /></Protected>} />
            <Route path="/admin/settings" element={<Protected requireAdmin><AdminSettings /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
